import { useCallback } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';
import { XMarkIcon, CreditCardIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import tw, { styled } from 'twin.macro';

import { ButtonV2 } from '@restworld/ui-ds';
import { Logo } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';
import { getAssetSrc, customImageLoader } from '@restworld/utils-common';
import { LayoutWrapper } from '../../layouts';

export function InvoiceBlockPage() {
  const router = useRouter();
  const { t: translateJobPositions } = useTranslation('job-positions-page');
  const { showMessages } = useIntercom();
  const { user } = useAuth();

  const handleGoHome = useCallback(() => {
    router.push('/employer/homepage');
  }, [router]);

  const handlePayInvoice = useCallback(() => {
    router.push('/employer/payments-and-invoices');
  }, [router]);

  const handleContactSupport = useCallback(() => {
    showMessages();
  }, [showMessages]);

  const userName = user?.name || translateJobPositions('user');

  return (
    <LayoutWrapper hideNavigation title="Bloccato" hasNavbar={false} hasFooter={false}>
      <Header>
        <HeaderContent>
          <Logo variant="mark" title="Restworld" className="h-8 w-8 text-purple-700" />
          <CloseButton onClick={handleGoHome}>
            <XMarkIcon tw="w-5 h-5 text-gray-700" />
          </CloseButton>
        </HeaderContent>
      </Header>

      <ContentWrapper>
        <TitleSection>
          <Title>{translateJobPositions('invoice_block_title', { name: userName })}</Title>
        </TitleSection>

        <MessageContainer>
          <MessageText>{translateJobPositions('invoice_block_message_part1')}</MessageText>
          <MessageText>{translateJobPositions('invoice_block_message_part2')}</MessageText>
        </MessageContainer>

        <IllustrationWrapper>
          <Image
            src={getAssetSrc('/utils-assets/payment-block-illustration.png')}
            alt="Payment illustration"
            width={608}
            height={492}
            loader={customImageLoader}
          />
        </IllustrationWrapper>
      </ContentWrapper>

      <FooterActions>
        <FooterContent>
          <ButtonV2
            variant="primary"
            size="md"
            onClick={handlePayInvoice}
            twStyle={tw`w-full justify-center`}
            StartIcon={CreditCardIcon}
          >
            {translateJobPositions('pay_invoice')}
          </ButtonV2>
          <ButtonV2
            variant="tertiary"
            size="md"
            onClick={handleContactSupport}
            twStyle={tw`w-full justify-center`}
            StartIcon={ChatBubbleLeftRightIcon}
          >
            {translateJobPositions('contact_customer_support')}
          </ButtonV2>
        </FooterContent>
      </FooterActions>
    </LayoutWrapper>
  );
}

const Header = styled.div`
  ${tw`w-full px-4 py-3 border-b border-neutral-40 flex justify-center items-center fixed top-0 bg-neutral-10 z-[99]`}
`;

const HeaderContent = styled.div`
  ${tw`w-full flex flex-row items-center justify-between max-w-[640px] px-0 sm:px-4`}
`;

const CloseButton = styled.button`
  ${tw`flex items-center justify-center w-10 h-10 rounded-lg hover:bg-neutral-20 transition-colors`}
`;

const ContentWrapper = styled.div`
  ${tw`pt-[80px] pb-[140px] min-h-screen bg-neutral-10 flex flex-col gap-8 max-w-[640px] mx-auto px-4`}
`;

const TitleSection = styled.div`
  ${tw`flex flex-col w-full`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-semibold text-gray-900`}
`;

const MessageContainer = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
`;

const MessageText = styled.p`
  ${tw`text-base font-medium text-gray-900 leading-normal`}
`;

const IllustrationWrapper = styled.div`
  ${tw`flex items-center justify-center w-full bg-purple-50 rounded-2xl px-7 py-4`}

  img {
    ${tw`w-full h-auto max-w-[608px]`}
  }
`;

const FooterActions = styled.div`
  ${tw`w-full py-3 border-t border-neutral-40 bg-neutral-10 fixed bottom-0 z-[100] flex justify-center`}
`;

const FooterContent = styled.div`
  ${tw`w-full max-w-[640px] px-4 flex flex-col gap-2.5`}
`;
